import { inject, Injectable } from '@angular/core';
import { type Dictionary } from '@ngrx/entity';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { produce } from 'immer';
import {
  catchError,
  combineLatest,
  concatMap,
  EMPTY,
  first,
  map,
  type Observable,
  of,
  switchMap,
  throwError,
} from 'rxjs';

import {
  type FormGroupSubmitActionUpdate,
  runtimeSelectors,
} from '@blaze-runtime/engine-data-access';
import {
  type CanvasElement,
  type CanvasElementFrom,
  type CanvasNamespaceId,
  FIELD_INFO_TYPE_COMMIT,
  getFormGroupLikeWrapper,
  getFormGroupLikeWrapperMainFormId,
  type WrapperRefs,
} from '@blaze-runtime/engine-domain';
import { getWorkflowNodeBase } from '@blaze-runtime/workflow-domain';
import { getActiveBinding } from '@blaze-shared/elements-domain';
import {
  type Connection,
  hasResultMessages,
  MAIN_INPUT_NAME,
  materializeWorkflowModelMutably,
  spliceWorkflowNodes,
  type Workflow,
  type WorkflowNode,
  type WorkflowNodeBase,
} from '@blaze-shared/workflow-domain';

import { fromEngine, type State } from '../../+state';
import { hasDataProps } from '../../canvas-elements/element-model/element-props-guards';
import { isWizard } from '../../canvas-elements/element-model/element-type-guards';
import { getWrapperRefs } from '../../canvas-elements/element-model/wrapper-refs';
import { DEFAULT_SUCCESS_MESSAGE } from '../../elements/form-container/form-container.model';
import {
  findSubmitWorkflowConnection,
  getOrCreateConnection,
  getUiRuntimeElement,
  getWorkflowRuntimeElement,
} from '../../utils/runtime';
import { FormGroupUtilsService } from '../form-group-utils.service';
import { WorkflowCrudService } from '../workflow-crud.service';

import {
  FormGroupActionNodesService,
  FormGroupWorkflowGenerationIgnorableError,
} from './form-group-action-nodes.service';
import { formGroupDataSourceInput } from './form-group-naming';

/** The input name under which a wizard reports which of its steps have been filled in. */
const FORMS_STATUS = 'formsFilled';

/** One dataset the form group writes to, and the forms that collect it. */

/**
 * Builds the workflow that saves a form group or wizard: every form it hosts, in the order their
 * datasets depend on each other.
 *
 * Forms whose key lives on the group's own dataset must be written first, so their new ids can be
 * assigned into the group's row; forms holding a key back to the group must be written after it,
 * for the same reason in reverse. That ordering is the whole point of generating this workflow
 * rather than one per form.
 *
 * **Note:** neither the workflow nor the connections are put into the state here.
 */
@Injectable({ providedIn: 'root' })
export class FormGroupSubmitWorkflowService {
  private readonly formGroupActionNodesService = inject(FormGroupActionNodesService);
  private readonly formGroupUtilsService = inject(FormGroupUtilsService);
  private readonly store = inject(Store<State>);
  private readonly workflowCrudService = inject(WorkflowCrudService);

  createOrUpdateFormGroupLikeSubmitWorkflowAndConnections(
    projectId: string | undefined,
    namespaceId: CanvasNamespaceId,
    formGroup: CanvasElementFrom<'formGroup' | 'wizard'>,
  ): Observable<FormGroupSubmitActionUpdate | undefined> {
    if (!projectId) {
      return of(undefined);
    }

    const refs = getWrapperRefs(formGroup);
    const buttonId: string | undefined = refs[FIELD_INFO_TYPE_COMMIT];
    const formsIds = getFormGroupLikeWrapper(refs).map(({ formId }) => formId);
    const { mainFormId } = getFormGroupLikeWrapperMainFormId(refs);
    if (!mainFormId) {
      return of(undefined);
    }

    return combineLatest({
      connections: this.store.select(runtimeSelectors.selectConnections),
      workflows: this.store.select(runtimeSelectors.selectWorkflows),
    }).pipe(
      first(),
      switchMap(({ connections, workflows }) => {
        const mainFormOutputConnection = findSubmitWorkflowConnection(
          connections,
          mainFormId,
          buttonId,
        );
        // The main form is recreated (new element id) whenever its slot is rebuilt - e.g. a
        // Form <-> Questionnaire mode switch - so a connection tied to it is never found
        // after such a rebuild, even though the wizard's own submit/status connections to
        // the workflow stay untouched (only child forms get removed, not the wizard).
        // Falling back to those stops us from creating a second workflow while the wizard
        // still has an old submit connection pointing at the original one.
        const wizardOutputConnection =
          !mainFormOutputConnection && isWizard(formGroup)
            ? connections.find(
                (c) =>
                  c.source.layerType === 'Ui' &&
                  c.source.id === formGroup.id &&
                  c.target.layerType === 'Workflow' &&
                  (c.eventType === 'submit' || c.eventType === 'status'),
              )
            : undefined;
        const existingConnection = mainFormOutputConnection ?? wizardOutputConnection;
        const existingWf = existingConnection
          ? workflows.find((wf) => wf.id === existingConnection.target.id)
          : undefined;
        return existingWf
          ? of(existingWf)
          : this.workflowCrudService.createWorkflow({
              inputs: {},
              inputSchemas: [],
              isLongRunning: false,
              isServer: false,
              nodes: [],
              pageId: namespaceId,
              projectId,
              startNodeId: null,
            });
      }),
      // Creating the workflow is asynchronous, so re-read the connections before wiring them up.
      concatLatestFrom(() => [this.store.select(fromEngine.selectConnectionsAndEntities)]),
      concatMap(([workflow, { connections, entities }]) => {
        const buttonConnection =
          buttonId !== undefined
            ? getOrCreateConnection(
                connections,
                namespaceId,
                getUiRuntimeElement(buttonId),
                getWorkflowRuntimeElement(workflow.id),
                'click',
              )
            : isWizard(formGroup)
              ? getOrCreateConnection(
                  connections,
                  namespaceId,
                  getUiRuntimeElement(formGroup.id),
                  getWorkflowRuntimeElement(workflow.id),
                  'submit',
                )
              : undefined;
        const formsConnections: Connection[] = formsIds.map((formId) =>
          getOrCreateConnection(
            connections,
            namespaceId,
            getUiRuntimeElement(formId),
            getWorkflowRuntimeElement(workflow.id),
            'change',
          ),
        );
        const formsStatusConnection = isWizard(formGroup)
          ? getOrCreateConnection(
              connections,
              namespaceId,
              getUiRuntimeElement(formGroup.id),
              getWorkflowRuntimeElement(workflow.id),
              'status',
            )
          : undefined;

        const updatedWorkflow = produce(workflow, (draft) => {
          draft.preventAutoStart = true;
          draft.inputs = this.buildInputs({
            buttonConnection,
            connections,
            entities,
            existingInputs: draft.inputs,
            formsConnections,
            formsStatusConnection,
            mainFormId,
            refs,
          });

          const oldActions = draft.nodes.filter((n) => n.id.match(/^action_/)) ?? [];
          const positionPlaceholder = this.clearOldActionNodes(
            draft,
            oldActions,
            entities,
            mainFormId,
          );
          const newActionNodes = materializeWorkflowModelMutably(
            this.formGroupActionNodesService.buildActionModel({
              entities,
              formGroup,
              oldActions,
              refs,
            }),
          );

          const firstNodeWithMessage = newActionNodes.find(hasResultMessages);
          if (firstNodeWithMessage && formGroup.type !== 'wizard') {
            firstNodeWithMessage.successMessage =
              oldActions
                .map((node) => (hasResultMessages(node) ? node.successMessage : undefined))
                .find(Boolean) ?? DEFAULT_SUCCESS_MESSAGE;
          }

          spliceWorkflowNodes(
            draft,
            draft.nodes.includes(positionPlaceholder) ? positionPlaceholder : null,
            newActionNodes,
          );
        });

        const newConnections: (Connection | undefined)[] = [
          ...(formsConnections || []),
          buttonConnection,
          formsStatusConnection,
        ];
        return this.workflowCrudService.updateWorkflow(updatedWorkflow, newConnections).pipe(
          map((savedWorkflow) => ({
            workflow: savedWorkflow,
            buttonConnection,
            formsConnections,
            formsStatusConnection,
          })),
        );
      }),
      catchError((e) =>
        e instanceof FormGroupWorkflowGenerationIgnorableError ? EMPTY : throwError(() => e),
      ),
    );
  }

  /**
   * Names every connection feeding the workflow, keeping the inputs of connections it does not
   * name — those belong to nodes the author added.
   */
  private buildInputs({
    buttonConnection,
    connections,
    entities,
    existingInputs,
    formsConnections,
    formsStatusConnection,
    mainFormId,
    refs,
  }: {
    buttonConnection: Connection | undefined;
    connections: Connection[];
    entities: Dictionary<CanvasElement>;
    existingInputs: Record<string, string>;
    formsConnections: Connection[];
    formsStatusConnection: Connection | undefined;
    mainFormId: string;
    refs: WrapperRefs;
  }): Record<string, string> {
    const inputConnectionPairs = [
      ...(buttonConnection ? [['submitButton', buttonConnection.id]] : []),
      ...(formsStatusConnection ? [[FORMS_STATUS, formsStatusConnection.id]] : []),
      ...formsConnections.map((formConnection) => {
        const form = entities[formConnection.source.id];
        const dataSourceName =
          form &&
          hasDataProps(form) &&
          getActiveBinding(form.props.data, 'value')?.dataSource?.name;
        if (!dataSourceName) {
          throw new Error('form binding incomplete');
        }
        return [
          formGroupDataSourceInput(dataSourceName, formConnection.source.id, refs),
          formConnection.id,
        ];
      }),
      [
        MAIN_INPUT_NAME,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        formsConnections.find((formConnection) => formConnection.source.id === mainFormId)!.id,
      ],
    ];
    const newConnectionIds = Object.values(inputConnectionPairs);
    // Preserve inputs the user wired up themselves, but only while their connection
    // still exists - inputs left behind by removed forms must not linger
    const preservedConnectionIds = Object.values(existingInputs).filter(
      (oid) =>
        !newConnectionIds.some(([, nid]) => nid === oid) &&
        connections.some((connection) => connection.id === oid),
    );
    return Object.fromEntries([
      ...Object.entries(existingInputs).filter(([, connectionId]) =>
        preservedConnectionIds.includes(connectionId),
      ),
      ...inputConnectionPairs,
    ]);
  }

  /**
   * Takes the previously generated action nodes out, leaving a placeholder where the main form's
   * node was so the regenerated nodes land in the same place in the author's workflow.
   */
  private clearOldActionNodes(
    draft: Workflow,
    oldActions: WorkflowNode[],
    entities: Dictionary<CanvasElement>,
    mainFormId: string,
  ): WorkflowNodeBase {
    const mainForm = entities[mainFormId];
    const mainSource = hasDataProps(mainForm)
      ? getActiveBinding(mainForm.props.data, 'value')?.dataSource
      : undefined;
    if (!mainSource) {
      throw new Error('main data source is missing');
    }
    const referenceNodeId = this.formGroupUtilsService.workflowNodeId('upsert', mainSource);
    const referenceNode =
      draft.nodes.find((n) => n.id === referenceNodeId) ??
      draft.nodes.find((n) => n.id.match(/^action_/));

    const positionPlaceholder: WorkflowNodeBase = getWorkflowNodeBase('Get', 'placeholder');
    oldActions.forEach((oldAction) => {
      if (draft.nodes.includes(oldAction)) {
        spliceWorkflowNodes(
          draft,
          oldAction,
          oldAction === referenceNode ? [positionPlaceholder] : [],
        );
      }
    });
    return positionPlaceholder;
  }
}
